import type { ContestCreateResponse } from '@betterpool/api-types/contests-service';
import { match } from 'ts-pattern';

import { reportEvent } from '~/domains/analytics';

import type { FormValues } from './types/FormValues';
import type { FormattedErrors } from './context/utils/formatZodErrors';
import type { TouchedValues } from './context/ContestManageContext';
import getFlattenValuesForEventReports from './utils/getFlattenValuesForEventReports';

export type ManageOperation = 'create' | 'edit' | 'restart';

export function reportRestartContestStart({
  contestTemplateName,
  contestType,
  contestLeague,
}: {
  contestTemplateName: string;
  contestType: string;
  contestLeague: string;
}) {
  reportEvent('Commissioner > Restart Contest - Start', {
    contestTemplateName,
    contestType,
    contestLeague,
  });
}

export function reportRestartContestAbandon({
  contestTemplateName,
  contestType,
  contestLeague,
  values,
  errors,
  touched,
}: {
  contestTemplateName: string;
  contestType: string;
  contestLeague: string;
  values: FormValues;
  errors: FormattedErrors;
  touched: TouchedValues;
}) {
  reportEvent('Commissioner > Restart Contest - Abandon', {
    contestTemplateName,
    contestType,
    contestLeague,
    values: getFlattenValuesForEventReports(values?.form ?? {}),
    errors: errors ? getFlattenValuesForEventReports((errors.form as FormattedErrors) ?? {}) : {},
    touched: touched
      ? getFlattenValuesForEventReports((touched.form as unknown as TouchedValues) ?? {})
      : {},
  });
}

export function reportRestartContestSuccess(data: ContestCreateResponse) {
  reportEvent('Commissioner > Restart Contest', {
    contest_name: data.contestName,
    contest_id: data.id,
    contest_template: data.contestTemplateId,
    contest_tags: data.tags,
    entry_fee: data.entryCost / 100,
    is_private: data.unlisted,
    payout_type: data.payoutType,
    max_entries: data.maxEntries,
    max_entries_per_user: data.maxEntriesPerUser,
    tags: data.tags.map((tag) => tag.name),
    is_entertainmnent_only: data.settings.entertainmentOnlySettings?.enabled,
    is_restarted: data.settings?.contestRestart?.isRestarted,
    restarted_origin: data.settings?.contestRestart?.origin,
    contest_type: data.contestType,
    contest_league: data.leagueAlias,
  });
}

export function reportRestartContestError(errorMessage: string) {
  reportEvent('Commissioner > Restart Contest - Error', {
    reason: errorMessage,
  });
}

export function reportCreateContestStart() {
  reportEvent('Commissioner > Create Contest - Start');
}

export function reportCreateContestAbandon({
  values,
  errors,
  touched,
}: {
  values: FormValues;
  errors: FormattedErrors;
  touched: TouchedValues;
}) {
  reportEvent('Commissioner > Create Contest - Abandon', {
    values: getFlattenValuesForEventReports(values ?? {}),
    errors: errors ? getFlattenValuesForEventReports(errors) : {},
    touched: touched ? getFlattenValuesForEventReports(touched) : {},
  });
}

export function reportCreateContestSuccess(data: ContestCreateResponse) {
  reportEvent('Commissioner > Create Contest', {
    contest_name: data.contestName,
    contest_id: data.id,
    contest_template: data.contestTemplateId,
    contest_tags: data.tags,
    entry_fee: data.entryCost / 100,
    is_private: data.unlisted,
    payout_type: data.payoutType,
    max_entries: data.maxEntries,
    max_entries_per_user: data.maxEntriesPerUser,
    tags: data.tags.map((tag) => tag.name),
    contest_type: data.contestType,
    contest_league: data.leagueAlias,
  });
}

export function reportCreateContestError(message: string) {
  reportEvent('Commissioner > Create Contest - Error', {
    reason: message,
  });
}

export function reportCreateContestNextSectionClick({
  sectionKey,
  values,
}: {
  sectionKey: string;
  values: FormValues;
}) {
  reportEvent('Commissioner > Create Contest - Next Section Click', {
    sectionKey,
    values: getFlattenValuesForEventReports(values ?? {}),
  });
}

export function reportManageContestStart({
  operation,
  contestTemplateName,
  contestType,
  contestLeague,
}: {
  operation: ManageOperation;
  contestTemplateName?: string;
  contestType?: string;
  contestLeague?: string;
}) {
  match(operation)
    .with('create', () => {
      reportCreateContestStart();
    })
    .with('edit', () => {
      reportEvent('Commissioner > Edit Contest - Start');
    })
    .with('restart', () => {
      reportRestartContestStart({
        contestTemplateName,
        contestType,
        contestLeague,
      });
    })
    .exhaustive();
}

export function reportManageContestAbandon({
  operation,
  contestTemplateName,
  contestType,
  contestLeague,
  values,
  errors,
  touched,
}: {
  operation: ManageOperation;
  contestTemplateName?: string;
  contestType?: string;
  contestLeague?: string;
  values: FormValues;
  errors: FormattedErrors;
  touched: TouchedValues;
}) {
  match(operation)
    .with('create', () => {
      reportCreateContestAbandon({ values, errors, touched });
    })
    .with('edit', () => {
      reportEvent('Commissioner > Edit Contest - Abandon', {
        values: getFlattenValuesForEventReports(values ?? {}),
        errors: errors ? getFlattenValuesForEventReports(errors) : {},
        touched: touched ? getFlattenValuesForEventReports(touched) : {},
      });
    })
    .with('restart', () => {
      reportRestartContestAbandon({
        contestTemplateName,
        contestType,
        contestLeague,
        values,
        errors,
        touched,
      });
    })
    .exhaustive();
}

export function reportManageContestSuccess(
  operation: ManageOperation,
  data: ContestCreateResponse
) {
  match(operation)
    .with('create', () => {
      reportCreateContestSuccess(data);
    })
    .with('edit', () => {
      reportEvent('Commissioner > Edit Contest', {
        contest_name: data.contestName,
      });
    })
    .with('restart', () => {
      reportRestartContestSuccess(data);
    })
    .exhaustive();
}

export function reportManageContestError(operation: ManageOperation, errorMessage: string) {
  match(operation)
    .with('create', () => {
      reportCreateContestError(errorMessage);
    })
    .with('edit', () => {
      reportEvent('Commissioner > Edit Contest Error', {
        reason: errorMessage,
      });
    })
    .with('restart', () => {
      reportRestartContestError(errorMessage);
    })
    .exhaustive();
}
